/** ====== Общие типы API-«конверта» ====== */
export type ApiStatus = "success" | "error";

/** Универсальный конверт: статус + код + полезная нагрузка */
export interface ApiEnvelope<T> {
  status: ApiStatus; // "success" | "error"
  status_code: string; // например "0000"
  entity: T; // полезная нагрузка
}

/** ====== Гранты ====== */
export type PasswordGrantType = "password";
export type RefreshGrantType = "refresh_token";
export type AuthGrantType = PasswordGrantType | RefreshGrantType;

/** ====== Запросы ====== */
export interface LoginRequest {
  username: string;
  password: string;
  grant_type?: PasswordGrantType;
  client_id?: string;
  client_secret?: string;
}

export interface RefreshTokenRequest {
  grant_type: RefreshGrantType;
  refresh_token: string;
  client_id?: string;
  client_secret?: string;
}

/** ====== Пользователь (расширенный, как в твоём JSON) ====== */
export interface UserEntity {
  id: number;
  school_id: number;
  external_id: string | number | null;
  group: string;

  firstname: string | null;
  lastname: string | null;
  patronymic: string | null;

  username: string | null;
  email: string | null;
  phone: string | null;

  photo: string | null;
  photo_thumb: {
    big: string | null;
    medium: string | null;
    small: string | null;
  };

  color: string | null;
  description: string | null;

  is_need_complete_challenge: boolean;
  is_online: boolean;
  status: number;

  school: {
    id: number;
    application_id: number | null;
    type: string;
    logo: string | null;
    logo_thumb: {
      big: string | null;
      medium: string | null;
      small: string | null;
      tiny: string | null;
      pixel: string | null;
    };
    name: string;
    short_description: string | null;
    full_description: string | null;
    website: string | null;
    phone: string | null;
    email: string | null;
    languages?: Array<{
      code: string;
      iso: string | null;
      name: string | null;
      is_default: number | boolean | null;
    }>;
    settings?: {
      isSdkUserOnly?: boolean;
      [key: string]: unknown;
    } | null;
  };

  teacher: unknown | null;
  student: unknown | null;

  /* Free-form per-tenant profile fields (requested explicitly by getUser).
     Olympiad registrations write the participant's class here, e.g.
     almaty_daryn_class_name / astana_daryn_class_name / vko_daryn_select_lang. */
  additional?: Record<string, unknown>;
}

/** Упрощённый пользователь для Login (если бэк шлёт user в ответе логина) */
export interface AuthUser {
  id: number;
  firstname: string;
  lastname: string;
  email: string;
  username?: string | null;
  photo?: string | null;
  group?: string | null;
  school?: {
    id: number;
    name: string;
    type?: string | null;
    logo?: string | null;
  };
  additional?: Record<string, unknown>;
}

export interface LoginResponseCore {
  access_token: string;
  token_type?: string;
  refresh_token?: string;
  ws_access_token?: string;
  expires_in?: number;
  user?: AuthUser; // иногда приходит сразу
}

export interface RefreshTokenResponseCore {
  access_token: string;
  refresh_token?: string;
  expires_in?: number;
  token_type?: string;
}

/**
 * ВАЖНО: некоторые эндпоинты могут возвращать ПЛОСКИЙ ответ (без конверта)
 * или ОБЁРНУТЫЙ в конверт {status, status_code, entity: {...}}.
 * Поддержим обе формы через union-типы ниже.
 */

/** Логин может быть: плоский ИЛИ в конверте */
export type LoginResponse = LoginResponseCore | ApiEnvelope<LoginResponseCore>;

/** Рефреш может быть: плоский ИЛИ в конверте */
export type RefreshTokenResponse =
  | RefreshTokenResponseCore
  | ApiEnvelope<RefreshTokenResponseCore>;

/** Эндпоинт профиля (как в твоём JSON) — строго в конверте */
export type MeResponse = ApiEnvelope<UserEntity>;

/** ====== Хелперы (по желанию) ====== */
export const unwrapEnvelope = <T>(data: T | ApiEnvelope<T>): T =>
  (data as ApiEnvelope<T>)?.entity !== undefined
    ? (data as ApiEnvelope<T>).entity
    : (data as T);
